/**
 * LinkButton — Contextual link/unlink/block/hide actions for a user
 *
 * Shows on MemberCard, PostCard author, etc.
 * Reads link status and renders appropriate action.
 */

import { useState } from 'react';
import type { ClubTheme } from './themes';
import { useCreateLink, useUnlink, useUnblock, useUnhide, useLinkStatus } from './queries';

interface LinkButtonProps {
  clubId: string;
  targetId: string;
  theme: ClubTheme;
  /** Show expanded actions (block/hide) in a dropdown */
  showMenu?: boolean;
}

export default function LinkButton({ clubId, targetId, theme, showMenu = false }: LinkButtonProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: status, isLoading } = useLinkStatus(clubId, targetId);
  const createLink = useCreateLink(clubId);
  const unlink = useUnlink(clubId);
  const unblock = useUnblock(clubId);
  const unhide = useUnhide(clubId);

  if (isLoading || !status) {
    return (
      <span
        className="inline-block h-7 w-16 animate-pulse rounded-lg"
        style={{ backgroundColor: `${theme.colors.muted}20` }}
      />
    );
  }

  const myLink = status.my_link;
  const isMutual = status.mutual;

  // Blocked state — show unblock option
  if (myLink === 'block') {
    return (
      <button
        type="button"
        onClick={() => unblock.mutate(targetId)}
        disabled={unblock.isPending}
        className="rounded-lg px-3 py-1 text-[11px] font-medium transition-colors"
        style={{
          backgroundColor: '#ef444420',
          color: '#ef4444',
        }}
      >
        {unblock.isPending ? '...' : 'Unblock'}
      </button>
    );
  }

  // Hidden state — show unhide option
  if (myLink === 'hide') {
    return (
      <button
        type="button"
        onClick={() => unhide.mutate(targetId)}
        disabled={unhide.isPending}
        className="rounded-lg px-3 py-1 text-[11px] font-medium transition-colors"
        style={{
          backgroundColor: `${theme.colors.muted}20`,
          color: theme.colors.muted,
        }}
      >
        {unhide.isPending ? '...' : 'Unhide'}
      </button>
    );
  }

  // Linked state — show unlink + mutual badge
  if (myLink === 'link') {
    return (
      <div className="relative flex items-center gap-1.5">
        {isMutual && (
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{
              backgroundColor: `${theme.colors.primary}20`,
              color: theme.colors.primary,
            }}
          >
            Mutual
          </span>
        )}
        <button
          type="button"
          onClick={() => {
            if (showMenu) {
              setMenuOpen(!menuOpen);
            } else {
              unlink.mutate(targetId);
            }
          }}
          disabled={unlink.isPending}
          className="rounded-lg px-3 py-1 text-[11px] font-medium transition-colors"
          style={{
            backgroundColor: `${theme.colors.primary}18`,
            color: theme.colors.primary,
            border: `1px solid ${theme.colors.primary}30`,
          }}
        >
          {unlink.isPending ? '...' : 'Linked'}
        </button>

        {/* Dropdown menu for unlink/block */}
        {showMenu && menuOpen && (
          <div
            className="absolute right-0 top-full z-20 mt-1 min-w-[140px] rounded-lg py-1 shadow-lg"
            style={{
              backgroundColor: theme.colors.surface,
              border: `1px solid ${theme.colors.muted}30`,
            }}
          >
            <button
              type="button"
              onClick={() => { unlink.mutate(targetId); setMenuOpen(false); }}
              className="block w-full px-3 py-1.5 text-left text-xs hover:opacity-80"
              style={{ color: theme.colors.text }}
            >
              Unlink
            </button>
            <button
              type="button"
              onClick={() => { createLink.mutate({ target_id: targetId, link_type: 'block' }); setMenuOpen(false); }}
              className="block w-full px-3 py-1.5 text-left text-xs hover:opacity-80"
              style={{ color: '#ef4444' }}
            >
              Block
            </button>
          </div>
        )}
      </div>
    );
  }

  // Not linked — show Link button + optional menu for hide/block
  return (
    <div className="relative flex items-center gap-1">
      <button
        type="button"
        onClick={() => createLink.mutate({ target_id: targetId, link_type: 'link' })}
        disabled={createLink.isPending}
        className="rounded-lg px-3 py-1 text-[11px] font-medium transition-colors"
        style={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.bg,
        }}
      >
        {createLink.isPending ? '...' : 'Link'}
      </button>

      {showMenu && (
        <>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-lg px-1.5 py-1 text-[11px] transition-colors"
            style={{ color: theme.colors.muted }}
          >
            &#x22EE;
          </button>

          {menuOpen && (
            <div
              className="absolute right-0 top-full z-20 mt-1 min-w-[140px] rounded-lg py-1 shadow-lg"
              style={{
                backgroundColor: theme.colors.surface,
                border: `1px solid ${theme.colors.muted}30`,
              }}
            >
              <button
                type="button"
                onClick={() => { createLink.mutate({ target_id: targetId, link_type: 'hide' }); setMenuOpen(false); }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:opacity-80"
                style={{ color: theme.colors.muted }}
              >
                Hide
              </button>
              <button
                type="button"
                onClick={() => { createLink.mutate({ target_id: targetId, link_type: 'block' }); setMenuOpen(false); }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:opacity-80"
                style={{ color: '#ef4444' }}
              >
                Block
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
